/**
 * AnyClip — 5중 하이브리드 티켓충전 모달
 * A: 친구 초대 (+5)
 * B: 스폰서 미션 1 (+1) — 알리 전자기기/패션
 * C: 스폰서 미션 2 (+1) — 알리 생활용품/핫딜
 * D: Stars 결제 (+N)
 */

import React, { useState } from 'react';
import { useApp, ACTIONS } from '../context/AppContext';
import { useI18n } from '../context/I18nContext';
import { requestStarsInvoice, submitVideoReward } from '../services/api';
import AliProductCard from './AliProductCard';
import './ReferralModal.css';

function ReferralModal() {
  const { state, dispatch } = useApp();
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [starsAmount, setStarsAmount] = useState(1);
  const [ticketJustEarned, setTicketJustEarned] = useState(false);

  if (state.status !== 'referral_locked') return null;

  const telegramId = state.user?.id || 'dev_user_001';
  const displayReferralLink = state.referralLink || `https://t.me/Video_Downloader_Fast_bot?start=ref_${telegramId}`;

  // 레퍼럴 링크 클립보드 복사
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayReferralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const el = document.createElement('textarea');
      el.value = displayReferralLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // 텔레그램 공유
  const handleShare = () => {
    const shareText = encodeURIComponent(t('share_text') || 'AnyClip 🎬');
    const shareUrl = encodeURIComponent(displayReferralLink);
    const telegramShareUrl = `https://t.me/share/url?url=${shareUrl}&text=${shareText}`;
    if (window.Telegram?.WebApp?.openTelegramLink) {
      window.Telegram.WebApp.openTelegramLink(telegramShareUrl);
    } else {
      window.open(telegramShareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // 알리 클릭 후 티켓 지급 콜백
  const handleAliTicket = (tickets) => {
    dispatch({ type: ACTIONS.SET_TICKETS, payload: tickets });
    setTicketJustEarned(true);
    // 3초 후 자동으로 홈 화면으로 이동
    setTimeout(() => dispatch({ type: ACTIONS.RESET }), 3000);
  };

  // ⭐ Telegram Stars 결제
  const handleStars = async () => {
    setIsProcessing(true);
    try {
      const tg = window.Telegram?.WebApp;
      const initData = tg?.initData || '';
      await requestStarsInvoice(initData, telegramId, starsAmount);
      alert(t('stars_payment_sent').replace('{n}', starsAmount));
    } catch (err) {
      alert(t('stars_payment_failed'));
    } finally {
      setIsProcessing(false);
    }
  };

  // 📺 Monetag 비디오/팝업 시청 (무제한 보상)
  const handleVideoAd = async () => {
    // Monetag SDK가 로드되어 있는지 확인 (show_11192388 글로벌 함수)
    if (typeof window.show_11192388 !== 'function') {
      alert(t('error_ad_not_ready'));
      return;
    }
    
    setIsProcessing(true);
    try {
      const tg = window.Telegram?.WebApp;
      const initData = tg?.initData || '';
      
      // Monetag 광고 실행 (Promise 반환)
      await window.show_11192388();
      
      // 시청 성공 (에러 없이 await 통과 시)
      const res = await submitVideoReward(initData, telegramId);
      if (res.success) {
        dispatch({ type: ACTIONS.SET_TICKETS, payload: res.tickets });
        setTicketJustEarned(true);
        setTimeout(() => dispatch({ type: ACTIONS.RESET }), 3000);
      }
    } catch (err) {
      console.log('Monetag Ad failed or skipped:', err);
      // Monetag는 유저가 광고를 취소하거나 No ads available일 때 throw 할 수 있습니다.
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => dispatch({ type: ACTIONS.RESET });

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={t('ref_title')}>
      <div className="modal-card">
        {/* 아이콘 */}
        <div className="modal-icon-wrap">
          <div className="modal-icon">🎟️</div>
          <div className="modal-icon-ring" />
        </div>

        <h2 className="modal-title">{t('ref_title')}</h2>
        <p className="modal-desc">
          {t('ref_desc')}<br />{t('ref_desc2')}
        </p>

        {isProcessing ? (
          <div className="video-ad-simulator">
            <h3>{t('ref_sponsor_check')}</h3>
            <span className="btn-loading">
              <span className="btn-dot"/><span className="btn-dot"/><span className="btn-dot"/>
            </span>
          </div>
        ) : (
          <div className="reward-options">

            {/* 옵션 A: 친구 초대 */}
            <div className={`reward-option ${activeTab === 'invite' ? 'active' : ''}`}>
              <button className="reward-btn btn-invite"
                onClick={() => setActiveTab(activeTab === 'invite' ? null : 'invite')}>
                <span className="btn-icon">🎁</span>
                <span className="btn-text">{t('ref_opt_invite')}</span>
                <span className="reward-badge">+5</span>
              </button>
              {activeTab === 'invite' && (
                <div className="reward-content">
                  <div className="referral-link-box">
                    <span className="referral-link-text">{displayReferralLink}</span>
                  </div>
                  <div className="modal-actions">
                    <button className={`btn-copy ${copied ? 'btn-copy--done' : ''}`} onClick={handleCopy}>
                      {copied ? t('ref_copied') : t('ref_copy')}
                    </button>
                    <button className="btn-share" onClick={handleShare}>{t('ref_share')}</button>
                  </div>
                </div>
              )}
            </div>

            {/* 옵션 B: 스폰서 미션 1 — 알리 전자기기/패션 */}
            <div className={`reward-option ${activeTab === 'mission1' ? 'active' : ''}`}>
              <button className="reward-btn btn-cpa"
                onClick={() => setActiveTab(activeTab === 'mission1' ? null : 'mission1')}>
                <span className="btn-icon">🛍️</span>
                <span className="btn-text">{t('ref_mission1')}</span>
                <span className="reward-badge">+1</span>
              </button>
              {activeTab === 'mission1' && (
                <div className="reward-content">
                  <AliProductCard
                    type="mission1"
                    telegramId={telegramId}
                    onTicketEarned={handleAliTicket}
                  />
                </div>
              )}
            </div>

            {/* 옵션 C: Monetag 보상형 동영상 광고 (무제한) */}
            <div className={`reward-option ${activeTab === 'mission2' ? 'active' : ''}`}>
              <button className="reward-btn btn-video"
                onClick={() => setActiveTab(activeTab === 'mission2' ? null : 'mission2')}>
                <span className="btn-icon">📺</span>
                <span className="btn-text">{t('ref_opt_video_btn')}</span>
                <span className="reward-badge">+1</span>
              </button>
              {activeTab === 'mission2' && (
                <div className="reward-content" style={{ textAlign: 'center', padding: '16px' }}>
                  <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: t('ref_video_desc') }} />
                  <button 
                    onClick={handleVideoAd}
                    disabled={isProcessing}
                    style={{
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '15px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(16,185,129,0.3)',
                      width: '100%',
                    maxWidth: '250px'
                  }}
                >
                  {isProcessing ? t('btn_loading') : t('btn_ad_start')}
                </button>
                </div>
              )}
            </div>

            {/* 옵션 D: Telegram Stars */}
            <div className={`reward-option ${activeTab === 'stars' ? 'active' : ''}`}>
              <button className="reward-btn btn-stars"
                onClick={() => setActiveTab(activeTab === 'stars' ? null : 'stars')}>
                <span className="btn-icon">⭐</span>
                <span className="btn-text">{t('ref_opt_stars')}</span>
                <span className="reward-badge stars-badge">1:1</span>
              </button>
              {activeTab === 'stars' && (
                <div className="reward-content stars-content">
                  <p className="stars-desc">{t('ref_stars_desc')}</p>
                  <div className="stars-selector">
                    {[1, 3, 5, 10].map(n => (
                      <button key={n}
                        className={`stars-btn ${starsAmount === n ? 'selected' : ''}`}
                        onClick={() => setStarsAmount(n)}>
                        ⭐{n}
                      </button>
                    ))}
                  </div>
                  <button className="btn-stars-pay" onClick={handleStars}>
                    ⭐ {starsAmount} Star → {starsAmount} {t('tickets_left').replace('🎟️ ', '').replace(': ', '')}
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

        {/* 티켓 획득 후 홈 이동 버튼 */}
        {ticketJustEarned && (
          <div style={{ marginTop: '16px', animation: 'cardIn 0.3s ease' }}>
            <button
              onClick={() => dispatch({ type: ACTIONS.RESET })}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                color: '#fff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(79,70,229,0.4)',
              }}
            >
              {t('btn_goto_download')}
            </button>
            <p style={{ color: '#a0aec0', fontSize: '12px', marginTop: '8px' }}>
              🎟️ {t('tickets_left')}{state.tickets} • {t('ref_stars_desc').split('(')[0].trim()}
            </p>
          </div>
        )}

        <button className="modal-close" onClick={handleClose} aria-label={t('btn_retry')} disabled={isProcessing}>✕</button>
      </div>
    </div>
  );
}

export default ReferralModal;
